import { createSelector } from 'reselect'
import { cloneDeep, filter, first, includes, isEmpty, some, sortBy } from 'lodash-es'
import { parse } from 'query-string'
import { parseFormatDropdownToArray } from 'utils/helper'
import { getAgeLimitRule } from 'utils/transactionAgeLimit'
import { isFinanceOnboardingActive } from 'app-redux/financeOnboardingSlice'
import { checkPermission, getModulePermissions } from './user/selectors'

export const initsSelector = (state) => state.inits
export const loadedInitSelector = (state) => initsSelector(state).loadedInit
export const settingsSelector = (state) => state.settings
export const mobileViewSelector = (state) => settingsSelector(state).isMobileView
const selectFinance = (state) => state.finance
export const tabViewSelector = (state) => settingsSelector(state).isTabView
export const warehouseSelector = (state) => state.finance.warehouse
export const warehousesSelector = (state) => warehouseSelector(state).warehouses
export const warehousesHasRoleSelector = (state) => warehouseSelector(state).warehouses_has_role
export const taxesSelector = (state) => selectFinance(state).tax.taxes
export const taxSearchTextSelector = (state) => selectFinance(state).tax.filter.searchText
export const activeTaxesSelector = createSelector(taxesSelector, (taxes) =>
  taxes.filter((tax) => tax.is_active),
)
export const optionsSelector = (state) => state.options.options
export const shippingCompSelector = (state) => selectFinance(state).shippingComp
export const shippingCompsSelector = (state) => shippingCompSelector(state).shippingComps
export const productCategoriesSelector = (state) => selectFinance(state).productCategories
export const translationSelector = (state) => state.translation
export const titleSelector = createSelector(
  translationSelector,
  (__, name) => name,
  (translation, name) => (translation.menu ? translation.menu[name] : ''),
)
// Nilai milestone onboarding dari backend (option `finance_onboarding`, dari /inits).
export const financeOnboardingValueSelector = (state) => state.options.options?.finance_onboarding
// Panduan berjalan hanya bila milestone perusahaan belum selesai (new/invoice_created/
// whatsapp_sent), billing tidak expired (layout memaksa ke halaman billing, bentrok dengan kunci
// navigasi panduan), dan user boleh membuat tagihan (staf tanpa akses tidak bisa mengikutinya).
export const isFinanceOnboardingEnabledSelector = (state) =>
  isFinanceOnboardingActive(state.options.options?.finance_onboarding) &&
  !state.billing?.is_expired &&
  checkPermission(state, 'invoices_add')
// Seperti di atas dan tidak sedang ditunda ("Nanti saja"). Dipakai untuk mengunci/menyaring UI
// selama panduan.
export const isFinanceOnboardingSelector = (state) =>
  isFinanceOnboardingEnabledSelector(state) && !state.financeOnboarding.snoozed
export const financeOnboardingStepSelector = (state) => state.financeOnboarding.step
export const financeOnboardingSnoozedSelector = (state) => state.financeOnboarding.snoozed
export const reportTitleSelector = createSelector(
  translationSelector,
  (__, name) => name,
  (translation, name) => (translation.reportmenu ? translation.reportmenu[name] : ''),
)
export const termTitleSelector = (state) => translationSelector(state).term
export const translationMenuSelector = (state) => translationSelector(state).menu

export const financeConfigSelector = (state) => selectFinance(state).config
export const bankTransStatusSelector = (state) => financeConfigSelector(state).bank_tran_status
export const contactTypeSelector = (state) => financeConfigSelector(state).contact_type
export const contactSelector = (state) => financeConfigSelector(state).contact
export const desktopLayoutSelector = createSelector(
  [mobileViewSelector, tabViewSelector],
  (isMobileView, isTabView) => !isMobileView && !isTabView,
)
export const auditModalSelector = (state) => selectFinance(state).auditModal
export const accountTransReportSelector = (state) => selectFinance(state).accountTransactionReport
export const routerSelector = (state) => state.router
export const routerMatchSelector = (state) => routerSelector(state).match
export const locationSelector = (state) => routerSelector(state).location
export const parseLocationSearchSelector = createSelector(locationSelector, (location) =>
  parse(location.search),
)
export const termSelector = (state) => selectFinance(state).expenseTerm
export const findTermSelector = createSelector(
  termSelector,
  (__, id) => id,
  (terms, id) => terms.expenseTerms.find((tax) => tax.id === id),
)
export const activeTermsSelector = createSelector(termSelector, (terms) =>
  terms.expenseTerms.filter((term) => term.is_active),
)
export const countActiveTermSelector = createSelector(activeTermsSelector, (terms) => terms.length)
export const canDeletedTermsSelector = (state) => termSelector(state).canDeletedExpenseTerms

export const firstTermSelector = createSelector(activeTermsSelector, (terms) =>
  !isEmpty(terms) ? first(terms) : {},
)
// Induk-only: baris rak (parent_warehouse_id != null) tidak pernah jadi gudang default
export const firstWarehouseSelector = createSelector(warehousesHasRoleSelector, (warehouses) => {
  const unArchiveWarehouses = (warehouses || []).filter(
    (row) => row.is_archive !== 1 && (row.parent_warehouse_id ?? null) === null,
  )
  const firstWarehouse = first(unArchiveWarehouses)
  if (!isEmpty(firstWarehouse)) {
    return firstWarehouse
  }
  return {}
})
// Induk-only: rak tidak pernah jadi gudang default, sehingga ambang `length <= 1`
// di bawah dihitung dari jumlah gudang induk
export const firstWarehouseWithRoleSelector = createSelector(
  warehousesHasRoleSelector,
  firstWarehouseSelector,
  (warehouses, firstWarehouseActive) => {
    const unArchiveWarehouses = (warehouses || []).filter((row) => {
      return (
        row.is_archive !== 1 &&
        isEmpty(row.contact_groups) &&
        (row.parent_warehouse_id ?? null) === null
      )
    })
    if (unArchiveWarehouses.length <= 1) {
      return firstWarehouseActive
    }
    const firstWarehouse = first(unArchiveWarehouses)
    if (!isEmpty(firstWarehouse)) {
      return firstWarehouse
    }
    return {}
  },
)
export const unitsSelector = (state) => selectFinance(state).unit.units
export const unitFilterSelector = (state) => selectFinance(state).unit.filter
export const canDeletedUnitsSelector = (state) => selectFinance(state).unit.canDeletedUnits
export const broadcastSelector = (state) => state.broadcast
export const billingSelector = (state) => state.billing
export const billingPosSelector = (state) => state.billingPos
export const loginFormSelector = (state) => state.loginForm
export const networkSelector = (state) => state.network
export const noInternetModalSelector = (state) => networkSelector(state).showNoInternetModal
const selectCompanies = (state) => state.companies
export const companiesSelector = (state) => selectCompanies(state).companies
export const selectedEndpointSelector = (state) => selectCompanies(state).selectedEndpoint

export const hiddenFeatureSelector = createSelector(
  billingSelector,
  (billing) => billing.hidden_feature || [],
)
export const hiddenFeatureEliteSelector = createSelector(
  billingSelector,
  (billing) => billing.hidden_feature_elite || [],
)

export const customColumnSelector = (state) => state.customColumn
export const multiCurrencySelector = (state) => state.multiCurrency
export const contactIdCardTypeSelector = (state) => selectFinance(state).idCardTypes

export const optionSelector = (state) => optionsSelector(state).option
export const optionByName = createSelector(
  optionsSelector,
  (__, name) => name,
  (result, name) => (!isEmpty(result) ? result[name] : null),
)
export const darkModeSelector = (state) => optionsSelector(state).dark_mode
export const invoiceLockMessagesSelector = (state) => optionsSelector(state).invoice_lock_messages
export const companySelector = (state) => state.companies
export const bankAccountsSelector = createSelector(
  selectFinance,
  (finance) => finance.bankAccounts || [],
)

export const activeBankAccountsSelector = createSelector(bankAccountsSelector, (accounts) =>
  accounts.filter((account) => !account.is_archive),
)

export const tempTransKeySelector = createSelector(
  parseLocationSearchSelector,
  (searchs) => searchs?.tempTransKey || '',
)
export const websiteIdSelector = (state) => selectCompanies(state).website_id

export const companyLogoSelector = (state) => optionsSelector(state).property_logo

export const companyDetailsSelector = createSelector(optionsSelector, (options) => {
  const detail = {
    property_name: options.property_name,
    property_email: options.property_email,
    property_phone: options.property_phone,
    property_address: options.property_address,
    property_address_delivery: options.property_address_delivery,
    property_country_id: options.property_country_id,
    property_npwp: options.property_npwp,
  }
  return detail
})

export const productCategoriesHasRolesSelector = (state) =>
  selectFinance(state).product_categories_has_role

export const financeApprovalSelector = (state) => selectFinance(state).financeApproval

export const paymentConnectProvidersSelector = (state) =>
  selectFinance(state).paymentConnectProviders
export const maintenanceSelector = (state) => state.maintenance
export const maxCompaniesSelector = (state) => state.max_companies
export const translationReportmenuSelector = (state) => translationSelector(state).reportmenu
export const translationReportSelector = (state) => translationSelector(state).report

export const accountCategoriesSelector = (state) => selectFinance(state).accountCategories

export const accountsHasRoleSelector = (state) => selectFinance(state).accounts_has_role

export const cashBankAccountCategoriesSelector = createSelector(
  accountCategoriesSelector,
  (accountCategories) => {
    const CASH_BANK = 1
    const CREDIT_CARD = 17
    return accountCategories.filter((row) => includes([CASH_BANK, CREDIT_CARD], row.id))
  },
)

export const isMultiCurrencySelector = (state) => optionsSelector(state).multi_currency === 1
const selectCurrencies = (state) => state.multiCurrency.currencies
export const baseMultiCurrencySelector = createSelector(
  [selectCurrencies, optionsSelector],
  (currencies, options) => {
    if (!Array.isArray(currencies) && options.multi_currency !== 1) {
      return {}
    }
    const findCurrency = currencies.find((item) => item.id === options.property_currency_id)
    return findCurrency || {}
  },
)

export const transactionFeesSelector = createSelector(
  selectFinance,
  (finance) => finance.fees || [],
)
export const makeTransactionFeeFilteredResults = () =>
  createSelector([transactionFeesSelector], (searchResults) => (search) => {
    return searchResults.filter((result) =>
      result.name?.toLowerCase()?.includes(search.toLowerCase()),
    )
  })

export const witholdingsSelector = createSelector(
  selectFinance,
  (finance) => finance.witholdings || [],
)
export const makeWitholdingFilteredResults = () =>
  createSelector([witholdingsSelector], (searchResults) => (search) => {
    return searchResults.filter((result) =>
      result.name?.toLowerCase()?.includes(search.toLowerCase()),
    )
  })

export const sortByCashBankSelector = (state) => optionsSelector(state).bank_account_sort_by
export const dashboardSelector = createSelector(selectFinance, (finance) => finance.dashboard || [])
export const outletsSelector = (state) => state.outlets

export const firstOutletSelector = createSelector(outletsSelector, (outlets) => {
  return first(outlets)
})

export const relatedReportSelector = createSelector(
  (state) => state._inits.user.menus?.related_report_menu,
  (__, name) => name,
  (relatedReportMenu, name) => relatedReportMenu?.[name] || [],
)

export const makeCashBanksResults = () =>
  createSelector(
    [bankAccountsSelector, dashboardSelector, optionsSelector, multiCurrencySelector],
    (bankAccounts, dashboard, options, multiCurrency) => (search, includeArchive) => {
      const {
        bank_account_sort_by: bankAccountSortBy,
        multi_currency: multiCurrencyOption,
        property_currency_symbol: propertyCurrencySymbol,
      } = options
      const currencies = multiCurrency?.currencies || []
      const isMultiCurrency = multiCurrencyOption === 1
      const searchToLowerCase = search.toLowerCase()
      // Sembunyikan akun archive saat toggle include_archive tidak aktif
      const visibleBankAccounts =
        includeArchive === 1 ? bankAccounts : bankAccounts.filter((a) => !a.is_archive)
      // Pertama urutkan bank accounts berdasarkan pengaturan di option
      const sortedBankAccounts = sortBy(visibleBankAccounts, bankAccountSortBy || 'ref_code')
      // Kemudian cari berdasarkan parameter search dan diambil dasboard id nya
      const data = sortedBankAccounts
        .filter((result) => {
          return (
            result.name?.toLowerCase()?.includes(searchToLowerCase) ||
            result.ref_code?.toLowerCase()?.includes(searchToLowerCase)
          )
        })
        .map((row) => {
          // Selanjutnya dicarikan datanya ke dasboard
          const findData = dashboard.find((item) => item.id === row.dashboard_id)
          const newRow = {
            ...row,
            currencySymbol: propertyCurrencySymbol,
            closing_balance: findData?.data?.closing_balance || 0,
            statement_balance: findData?.data?.statement_balance || 0,
            graph: findData?.data?.graph || {},
            loaded: findData?.loaded || false,
            daterange: findData?.daterange || '',
            lastCache: findData?.lastCache || 0,
            cache: findData?.cache || '',
          }
          if (isMultiCurrency && !!row.currency_id) {
            const findMultiCurrency = currencies.find((currency) => currency.id === row.currency_id)
            newRow.currencySymbol = findMultiCurrency?.symbol || propertyCurrencySymbol
          }
          return newRow
        })
      const isLoaded = some(data, (item) => item.loaded === true)
      const newData = { data, bankAccountSortBy, isMultiCurrency, isLoaded }

      return newData
    },
  )

export const makeSelectPermission = () => {
  const permissions = createSelector(
    [(state) => state, (state, resource) => resource, (state, recursive) => recursive],
    (state, resource, recursive) => getModulePermissions(state, resource, recursive),
  )
  return permissions
}

export const makeCheckPermission = () => {
  const granted = createSelector(
    [(state) => state, (state, permission) => permission],
    (state, permission) => checkPermission(state, permission),
  )
  return granted
}
export const loadingInitSelector = (state) => initsSelector(state).loading
export const showReportStatsSelector = (state) => selectFinance(state).showReportStats
export const membershipIsActiveSelector = (state) => !!selectFinance(state).membershipIsActive

const selectInits = (state) => state._inits
export const financeTransTotalSelector = (state) => selectInits(state).finance_trans_total
export const transTotalSelector = createSelector(
  financeTransTotalSelector,
  (__, key) => key,
  (transTotal, key) => {
    if (!isEmpty(transTotal)) {
      return transTotal[key]
    }
    return 0
  },
)

export const livestreamSelector = (state) => selectInits(state).livestream
export const userSelector = (state) => selectInits(state).user

const transactionAgeLimitSelector = (state) => selectFinance(state).transactionAgeLimit

// Aturan usia transaksi untuk user login: { age_value, age_unit }. null = tidak dibatasi.
// Sumber: finance.transaction_age_limit (inits). Tanggal cutoff sengaja tidak dihitung di sini,
// karena hasil selector di-memo dan tidak ikut maju saat ganti hari.
export const transactionBackdateRuleSelector = createSelector(
  [transactionAgeLimitSelector, userSelector],
  (ageLimit, user) => getAgeLimitRule(ageLimit, user, 'restrict_backdate'),
)
export const transactionModifyRuleSelector = createSelector(
  [transactionAgeLimitSelector, userSelector],
  (ageLimit, user) => getAgeLimitRule(ageLimit, user, 'restrict_modify'),
)
export const financeMarketplaceConnectsSelector = createSelector(
  selectFinance,
  (finance) => finance.marketplaceConnects || [],
)
export const marketplaceConnectsSelector = createSelector(
  [financeMarketplaceConnectsSelector, financeConfigSelector, userSelector],
  (marketplaceConnects, financeConfig, user) => {
    if (marketplaceConnects?.length) {
      return marketplaceConnects
    }
    if (financeConfig?.marketplace_connects?.length) {
      return financeConfig.marketplace_connects
    }
    if (user?.marketplace_connects?.length) {
      return user.marketplace_connects
    }
    return []
  },
)
export const menusSelector = createSelector(userSelector, (user) => user.menus || {})
export const showNPSSelector = (state) => selectInits(state).show_nps
export const hrisSelector = (state) => selectInits(state).show_hr_addon
export const tutorialsSelector = createSelector(selectInits, (inits) => inits.tutorials || {})
export const tutorialSelector = createSelector(
  tutorialsSelector,
  (__, key) => key,
  (tutorials, key) => tutorials[key] || {},
)
export const reviewsSelector = createSelector(selectInits, (inits) => inits.reviews || [])
export const defaultMessageSelector = createSelector(
  selectInits,
  (inits) => inits.message_default || [],
)
export const langSelector = createSelector(selectInits, (inits) => inits.lang || [])
export const salutationsSelector = createSelector(selectInits, (inits) => inits.salutations || [])
export const reasonsSelector = createSelector(selectInits, (inits) => inits.reasons || [])
export const announcementSelector = (state) => selectInits(state).announcement
export const bannerSelector = (state) => selectInits(state).banner
export const closedAppOnboardingNotificationIdsSelector = (state) =>
  selectInits(state).closedAppOnboardingNotificationIds || []
export const permissionsSelector = createSelector(userSelector, (user) => user.permissions || [])
export const contactTypesAccessSelector = createSelector(
  contactTypeSelector,
  selectInits,
  (contactTypes, _inits) => {
    const permissions = getModulePermissions({ _inits }, 'contacts')
    const _contactTypes = contactTypes?.filter((contactType) => {
      const contactTypesAccess = {
        1: permissions.contacts_vendor,
        2: permissions.contacts_employee,
        3: permissions.contacts_customer,
        4: permissions.contacts_other,
        5: permissions.contacts_investor,
      }
      return contactTypesAccess[contactType.id]
    })

    return _contactTypes
  },
)
export const menuSelector = (state) => state.menu
export const menuLeftDataSelector = (state) => menuSelector(state).menuLeftData
export const sidebarAppsSelector = (state) => menuSelector(state).sidebarApps
export const transTypeConfigSelector = (state) => financeConfigSelector(state).trans_type
export const formatDropdownAccountSelector = createSelector(optionsSelector, (options) => {
  const { format_dropdown_accounts: formatDropdownAccounts } = options
  return parseFormatDropdownToArray(formatDropdownAccounts)
})
export const formatDropdownProductSelector = createSelector(optionsSelector, (options) => {
  const { format_dropdown_products: formatDropdownProducts } = options
  return parseFormatDropdownToArray(formatDropdownProducts)
})
export const formatDropdownContactSelector = createSelector(optionsSelector, (options) => {
  const { format_dropdown_contacts: formatDropdownContacts } = options
  return parseFormatDropdownToArray(formatDropdownContacts)
})
export const formatDropdownProfitLossSelector = createSelector(optionsSelector, (options) => {
  const { format_account_profit_loss: formatDropdownProfitLoss } = options
  return parseFormatDropdownToArray(formatDropdownProfitLoss)
})
export const accountWatchListSelector = (state) => selectFinance(state).accountWatchList
export const showContactDetailMoneyChartSelector = (state) =>
  optionsSelector(state).show_contact_detail_money_chart === 1
export const shiftSelector = (state) => state.cashier.shift
export const outletSelector = createSelector(
  (state) => state.cashier.outlet,
  warehousesSelector,
  (outlet, warehouses) => {
    const _outlet = cloneDeep(outlet)
    if (!isEmpty(_outlet)) {
      if (isEmpty(_outlet.warehouse)) {
        _outlet.warehouse_id = 1
        const findWarehouse = warehouses.find((warehouse) => warehouse.id === 1)
        if (!isEmpty(findWarehouse)) {
          _outlet.warehouse = findWarehouse
        }
      }
    }
    return _outlet
  },
)
export const selectedOutletSelector = createSelector(
  outletsSelector,
  outletSelector,
  (outlets, outlet) => {
    if (isEmpty(outlet)) {
      return {}
    }
    const findOutlet = outlets.find((_outlet) => _outlet.id === outlet.id)
    if (isEmpty(findOutlet)) {
      return {}
    }
    return { ...findOutlet, warehouse: outlet.warehouse }
  },
)
export const isLockedSelector = (state) => state.cashier.isLocked
export const printReceiptCountActiveSelector = (state) => state.cashier.printReceiptCountActive
export const transferBankPreferenceSelector = (state) => state.cashier.transferBankPreference
export const posZeroLoadingSelector = (state) => !!state.cashier.posZeroLoading
export const catalogSyncedEndpointSelector = (state) => state.cashier.catalogSyncedEndpoint || ''
export const outletByCurrentUserSelector = createSelector(
  outletsSelector,
  userSelector,
  (outlets, user) => {
    return filter(outlets, (outlet) => {
      if (isEmpty(outlet.users) || !Array.isArray(outlet.users)) {
        return false
      }

      return outlet.users.some((_user) => _user.id === user.id)
    })
  },
)
export const invoiceTypesSelector = (state) => state.cashier.invoice_types
export const paymentTypesSelector = (state) => state.cashier.payment_type

export const selectedCompanySelector = createSelector(
  companiesSelector,
  websiteIdSelector,
  (companies, websiteId) => companies.find((company) => company.website_id === websiteId),
)

export const approvalIsEnabledSelector = createSelector(financeApprovalSelector, (options) => {
  // cek apakah ada salah satu value yang bernilai 1
  if (!isEmpty(options)) {
    return some(Object.values(options), (value) => value === 1)
  }

  return false
})

export const posOutletOptionsSelector = (state) => state.cashier.pos_outlet_options
export const selectedPosOutletOptionsSelector = createSelector(
  posOutletOptionsSelector,
  selectedOutletSelector,
  (__, outletId) => outletId,
  (posOutletOptions, selectedOutlet, outletId) => {
    if (isEmpty(selectedOutlet) || isEmpty(posOutletOptions)) {
      return {}
    }

    const _options = {}
    Object.keys(posOutletOptions).forEach((key) => {
      _options[key] = posOutletOptions[key][`${outletId || selectedOutlet?.id}`]
    })

    return _options
  },
)
export const closeShiftModalOpenSelector = (state) => state.cashier.closeShiftModalOpen
export const closeShiftModalSuccessSelector = (state) => state.cashier.closeShiftModalSuccess
export const printersSelector = (state) => state.cashier.hardwares.printers
export const cashierSessionSelector = (state) => state.cashier.session
export const customProductCashierSelector = (state) => state.cashier?.customProduct ?? {}
export const makePrinterFilteredResults = () =>
  createSelector([printersSelector, selectedOutletSelector], (printers, outlet) => (search) => {
    if (!search) {
      return printers.filter((row) => row.outlet_id === outlet.id)
    }
    return printers?.filter(
      (row) =>
        row.printer?.toLowerCase()?.includes(search.toLowerCase()) && row.outlet_id === outlet?.id,
    )
  })
export const firstOutletByCurrentUserSelector = createSelector(
  outletByCurrentUserSelector,
  (outlets) => {
    return first(outlets)
  },
)
export const selectedPosOutletPrintStyleLogoBase64Selector = createSelector(
  selectedPosOutletOptionsSelector,
  (posOutletOptions) => posOutletOptions?.print_style_logo_base64 || '',
)
export const selectedPosOutletPrintStyleLogoSelector = createSelector(
  selectedPosOutletOptionsSelector,
  (posOutletOptions) => posOutletOptions?.print_style_logo || '',
)
export const selectedTaxeOptionSelector = createSelector(
  optionsSelector,
  (options) => options.pos_tax_id,
)

export const showColumnsFinanceProductsSelector = (state) =>
  optionsSelector(state).show_columns_finance_products || ''

export const paymentConnectKledoSelector = (state) => selectFinance(state).payment_connect_kledo
export const paymentConnectKledoInfoDismissedSelector = (state) =>
  settingsSelector(state).payment_connect_kledo_info_dismissed

export const backButtonSelector = (state) => state.backButton
